import React, { useState } from 'react';
import '@livekit/components-styles';
import {
  LiveKitRoom,
  RoomAudioRenderer,
} from '@livekit/components-react';
import { useRoomContext, useParticipants, useLocalParticipant } from '@livekit/components-react';
import { PhoneForwarded, UserMinus, Mic, MicOff, PhoneOff } from 'lucide-react';

const serverUrl = process.env.REACT_APP_LIVEKIT_URL || process.env.REACT_APP_SERVER_URL || process.env.LIVEKIT_URL || 'ws://localhost:7880';
const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:8000' : '/api');
const tokenEndpoint = process.env.REACT_APP_TOKEN_ENDPOINT || `${API_BASE}/get-token`;

const getInitials = (nameOrIdentity: string) => {
  const cleanName = nameOrIdentity.replace(/^(agent-)/i, '').trim();
  if (cleanName.length <= 2) {
    return cleanName.toUpperCase();
  }
  // Try to split by spaces/dash to get first letter of first two words
  const parts = cleanName.split(/[\s_-]+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  // Fallback to first two letters
  return cleanName.slice(0, 2).toUpperCase();
};

const getParticipantDescription = (p: any) => {
  const identity = (p.identity || '').toUpperCase();
  const name = (p.name || '').toUpperCase();

  if (identity === 'DOT' || name === 'DOT') {
    return 'Banking Advisor Expert';
  }
  if (identity === 'SURA' || name === 'SURA') {
    return 'Insurance Advisor Expert';
  }
  if (identity === 'SAGE' || name === 'SAGE') {
    return 'Enterprise Advisor Expert';
  }
  if (identity === 'COORDINATOR' || name === 'COORDINATOR') {
    return 'System Coordinator';
  }
  if (p.metadata === 'agent') {
    return 'AI Assistant';
  }
  return 'User / Participant';
};

function ParticipantCard({ participant }: { participant: any }) {
  const isSpeaking = participant.isSpeaking;
  const identity = participant.identity || 'unknown';
  const name = participant.name || participant.identity || 'unknown';
  const isCoordinator = identity.toLowerCase() === 'coordinator' || name.toLowerCase() === 'coordinator' || participant.metadata === 'coordinator';
  const isAgent = participant.metadata === 'agent' || isCoordinator || ['DOT', 'SURA', 'SAGE'].includes(identity);

  const initials = getInitials(name);
  const description = getParticipantDescription(participant);

  // Use different gradient backgrounds based on agent type vs user
  let avatarGradient = 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)'; // Generic Purple/Indigo for user
  if (identity === 'DOT') {
    avatarGradient = 'linear-gradient(135deg, #10b981 0%, #059669 100%)'; // Emerald for DOT (Banking)
  } else if (identity === 'SURA') {
    avatarGradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'; // Amber for SURA (Insurance)
  } else if (identity === 'SAGE') {
    avatarGradient = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'; // Blue for SAGE (Enterprise)
  } else if (isCoordinator) {
    avatarGradient = 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'; // Gray for Coordinator
  } else if (isAgent) {
    avatarGradient = 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)'; // Pink for other AI
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 32px',
      borderRadius: '32px',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: isSpeaking ? '2.5px solid rgba(59, 130, 246, 0.7)' : '1px solid rgba(255, 255, 255, 0.05)',
      boxShadow: isSpeaking
        ? '0 16px 64px rgba(59, 130, 246, 0.25), inset 0 0 24px rgba(59, 130, 246, 0.1)'
        : '0 12px 40px rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(16px)',
      width: '100%',
      maxWidth: '380px',
      minHeight: '380px',
      boxSizing: 'border-box',
      transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      transform: isSpeaking ? 'scale(1.05)' : 'scale(1)',
      position: 'relative'
    }}>
      {/* Speaking Glow Effect */}
      {isSpeaking && (
        <span style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          display: 'flex',
          height: '10px',
          width: '10px'
        }}>
          <span className="ping-animation" style={{
            position: 'absolute',
            display: 'inline-flex',
            height: '100%',
            width: '100%',
            borderRadius: '50%',
            backgroundColor: '#3b82f6',
            opacity: 0.8
          }} />
          <span style={{
            position: 'relative',
            display: 'inline-flex',
            borderRadius: '50%',
            height: '10px',
            width: '10px',
            backgroundColor: '#3b82f6'
          }} />
        </span>
      )}

      {/* Circle Initials Avatar */}
      <div style={{
        width: 'clamp(110px, 12vw, 170px)',
        height: 'clamp(110px, 12vw, 170px)',
        borderRadius: '50%',
        background: avatarGradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isSpeaking
          ? '0 0 35px rgba(59, 130, 246, 0.7), inset 0 2px 8px rgba(255, 255, 255, 0.3)'
          : '0 8px 24px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.15)',
        marginBottom: '28px',
        border: '3px solid rgba(255, 255, 255, 0.12)',
        transition: 'all 0.3s ease'
      }}>
        <span style={{
          fontSize: 'clamp(40px, 4.5vw, 64px)',
          fontWeight: 800, // Bold
          color: '#ffffff',
          letterSpacing: '1px',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          fontFamily: "'Outfit', 'Inter', sans-serif"
        }}>
          {initials}
        </span>
      </div>

      {/* Name and Description */}
      <div style={{ textAlign: 'center', width: '100%' }}>
        <h4 style={{
          margin: '0 0 10px 0',
          fontSize: 'clamp(20px, 2.2vw, 26px)',
          fontWeight: 700,
          color: '#ffffff',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontFamily: "'Outfit', 'Inter', sans-serif"
        }}>
          {name}
        </h4>
        <span style={{
          fontSize: 'clamp(13px, 1.4vw, 16px)',
          color: '#8b949e',
          display: 'block',
          fontWeight: 500,
          fontFamily: "'Inter', sans-serif",
          lineHeight: '1.4'
        }}>
          {description}
        </span>
      </div>
    </div>
  );
}

function ParticipantGrid() {
  const participants = useParticipants();

  return (
    <div style={{
      flex: 1,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
      justifyContent: 'center',
      alignContent: 'center',
      justifyItems: 'center',
      padding: '48px 32px',
      overflowY: 'auto',
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      boxSizing: 'border-box'
    }}>
      {participants.length === 0 ? (
        <div style={{ gridColumn: '1 / -1', color: '#666', fontSize: '18px', fontFamily: "'Inter', sans-serif" }}>
          Waiting for participants to connect...
        </div>
      ) : (
        participants.map((p) => (
          <ParticipantCard key={p.identity} participant={p} />
        ))
      )}
    </div>
  );
}

function CustomControlBar({ setInRoom }: { setInRoom: (v: boolean) => void }) {
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const room = useRoomContext();

  const handleDisconnect = async () => {
    await room.disconnect();
    setInRoom(false);
  };

  const toggleMic = async () => {
    if (localParticipant) {
      await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      padding: '20px',
      backgroundColor: '#111115',
      borderTop: '1px solid #22222b',
      boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.4)'
    }}>
      <button
        onClick={toggleMic}
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: 'none',
          backgroundColor: isMicrophoneEnabled ? '#3b82f6' : '#ef4444',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'all 0.3s ease',
          boxShadow: isMicrophoneEnabled ? '0 0 15px rgba(59, 130, 246, 0.4)' : '0 0 15px rgba(239, 68, 68, 0.4)'
        }}
        title={isMicrophoneEnabled ? "Mute Microphone" : "Unmute Microphone"}
      >
        {isMicrophoneEnabled ? <Mic size={22} /> : <MicOff size={22} />}
      </button>
      <button
        onClick={handleDisconnect}
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: 'none',
          backgroundColor: '#dc2626',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'all 0.3s ease',
          boxShadow: '0 0 15px rgba(220, 38, 38, 0.4)'
        }}
        title="Leave Room"
      >
        <PhoneOff size={22} />
      </button>
    </div>
  );
}

const TEAMS_AGENT_OPTIONS = [
  { id: 'DOT', name: 'DOT', role: 'Banking Advisor', defaultName: 'DOT - Banking Advisor', icon: '🏦', color: '#3b82f6' },
  { id: 'SURA', name: 'SURA', role: 'Insurance Advisor', defaultName: 'SURA - Insurance Advisor', icon: '🛡️', color: '#10b981' },
  { id: 'SAGE', name: 'SAGE', role: 'Custom AI Advisor', defaultName: 'SAGE - Custom AI Advisor', icon: '🤖', color: '#8b5cf6' },
  { id: 'ALL', name: 'AgentHub Suite', role: 'All-in-One Suite', defaultName: 'AgentHub Enterprise Advisor', icon: '🌐', color: '#f59e0b' },
];

function TeamsJoinButton() {
  const [showModal, setShowModal] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('DOT');
  const [guestName, setGuestName] = useState('DOT - Banking Advisor');
  const [status, setStatus] = useState<'IDLE' | 'CONNECTING' | 'CONNECTED' | 'ERROR'>('IDLE');
  const [statusMsg, setStatusMsg] = useState('');

  const handleSelectAgent = (agent: typeof TEAMS_AGENT_OPTIONS[0]) => {
    setSelectedAgent(agent.id);
    setGuestName(agent.defaultName);
  };

  const handleJoinTeams = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!meetingUrl) return;

    setStatus('CONNECTING');
    setStatusMsg(`Launching Teams Bot for '${selectedAgent}' as '${guestName}'...`);

    try {
      const resp = await fetch(`${API_BASE}/teams/join-web`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meeting_url: meetingUrl, guest_name: guestName, agent_id: selectedAgent }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setStatus('CONNECTED');
        setStatusMsg(`Bot launching as '${guestName}'. Check Teams meeting!`);
      } else {
        setStatus('ERROR');
        setStatusMsg(data.detail || 'Failed to join Teams call');
      }
    } catch (err: any) {
      setStatus('ERROR');
      setStatusMsg(err.message || 'Error connecting to token server');
    }
  };

  const handleLeaveTeams = async () => {
    try {
      await fetch(`${API_BASE}/teams/leave`, { method: 'POST' });
      setStatus('IDLE');
      setStatusMsg('Bot disconnected from Teams call.');
    } catch (err: any) {
      console.error('Error leaving Teams call:', err);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        style={{
          padding: '5px 12px',
          borderRadius: '4px',
          border: 'none',
          backgroundColor: status === 'CONNECTED' ? '#059669' : '#4f46e5',
          color: 'white',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <PhoneForwarded size={14} />
        {status === 'CONNECTED' ? 'MS Teams Active' : 'Join MS Teams Meeting'}
      </button>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#18181b',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            width: '460px',
            maxWidth: '90vw',
            color: 'white',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Call Agent into MS Teams</h3>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>Select an agent and customize display name for Teams</span>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleJoinTeams} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* Agent Selection Grid */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af' }}>Select Available Agent</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {TEAMS_AGENT_OPTIONS.map(agent => {
                    const isSelected = selectedAgent === agent.id;
                    return (
                      <div
                        key={agent.id}
                        onClick={() => handleSelectAgent(agent)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: isSelected ? `2px solid ${agent.color}` : '1px solid #3f3f46',
                          backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.08)' : '#27272a',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>{agent.icon}</span>
                        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: isSelected ? 'white' : '#d1d5db' }}>{agent.name}</span>
                          <span style={{ fontSize: '11px', color: '#9ca3af', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{agent.role}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Display Name */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af' }}>Custom Display Name in Teams</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. DOT - Banking Advisor"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#27272a',
                    border: '1px solid #3f3f46',
                    color: 'white',
                    fontSize: '13px'
                  }}
                />
              </div>

              {/* Teams Join Link */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af' }}>Teams Meeting Join Link (URL)</label>
                <input
                  type="url"
                  required
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                  value={meetingUrl}
                  onChange={(e) => setMeetingUrl(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#27272a',
                    border: '1px solid #3f3f46',
                    color: 'white',
                    fontSize: '13px'
                  }}
                />
              </div>

              {statusMsg && (
                <div style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: status === 'ERROR' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                  color: status === 'ERROR' ? '#f87171' : '#60a5fa',
                  fontSize: '12px'
                }}>
                  {statusMsg}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  type="submit"
                  disabled={status === 'CONNECTING'}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {status === 'CONNECTING' ? 'Joining Teams...' : `Call ${selectedAgent} into Teams`}
                </button>

                {status === 'CONNECTED' && (
                  <button
                    type="button"
                    onClick={handleLeaveTeams}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Leave
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function RoomHeader({ roomName }: { roomName: string }) {
  return (
    <div style={{ padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#111115', borderBottom: '1px solid #22222b' }}>
      <h3 style={{ margin: 0, color: 'white', fontFamily: "'Outfit', 'Inter', sans-serif" }}>Room: {roomName}</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <AgentInviteButton persona="BANKING" label="Invite DOT (Banking)" />
          <AgentInviteButton persona="INSURANCE" label="Invite SURA (Insurance)" />
          <AgentInviteButton persona="SAGE" label="Invite SAGE (Enterprise)" />
          <TeamsJoinButton />
        </div>
      </div>
    </div>
  );
}

function AppContent({ roomName, setInRoom, token, livekitUrl }: { roomName: string, setInRoom: (v: boolean) => void, token: string, livekitUrl: string }) {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* @ts-ignore */}
      <LiveKitRoom
        video={false}
        audio={true}
        token={token}
        serverUrl={livekitUrl}
        connect={true}
        onError={(err) => {
          console.error("[LiveKitRoom Error]:", err);
          alert(`LiveKit Connection Error: ${err.message || err}`);
        }}
        onDisconnected={(reason) => {
          console.log("[LiveKitRoom] Disconnected from room. Reason:", reason);
          setInRoom(false);
        }}
        data-lk-theme="default"
        style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}
      >
        <RoomHeader roomName={roomName} />
        <ParticipantGrid />
        <RoomAudioRenderer />
        <CustomControlBar setInRoom={setInRoom} />
      </LiveKitRoom>
    </div>
  );
}

function App() {
  const [token, setToken] = useState<string | undefined>(undefined);
  const [livekitUrl, setLivekitUrl] = useState<string | undefined>(undefined);
  const [roomName, setRoomName] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [inRoom, setInRoom] = useState(false);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (roomName && participantName) {
      try {
        const response = await fetch(tokenEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ room_name: roomName, participant_name: participantName }),
        });
        const data = await response.json();
        const activeUrl = data.server_url || serverUrl;
        console.log(`[handleJoin] Fetched token for room ${roomName}. LiveKit URL: ${activeUrl}`);
        setToken(data.token);
        setLivekitUrl(activeUrl);
        setInRoom(true);
      } catch (error) {
        console.error('Error fetching token:', error);
        alert('Failed to connect to token server');
      }
    }
  };

  if (!inRoom || !token || !livekitUrl) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a1a', color: 'white', fontFamily: 'sans-serif' }}>
        <form onSubmit={handleJoin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '2rem', backgroundColor: '#2a2a2a', borderRadius: '8px', width: '300px' }}>
          <h2 style={{ textAlign: 'center' }}>UserHub Voice Chat</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label>Participant Name</label>
            <input
              type="text"
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              required
              placeholder="e.g. Alice"
              style={{ padding: '0.5rem', borderRadius: '4px', border: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label>Room Name</label>
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              required
              placeholder="e.g. my-room"
              style={{ padding: '0.5rem', borderRadius: '4px', border: 'none' }}
            />
          </div>
          <button type="submit" style={{ padding: '0.75rem', borderRadius: '4px', border: 'none', backgroundColor: '#0070f3', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
            Join Room
          </button>
        </form>
      </div>
    );
  }

  return <AppContent roomName={roomName} setInRoom={setInRoom} token={token} livekitUrl={livekitUrl} />;
}

function AgentInviteButton({ persona, label }: { persona: string, label: string }) {
  const room = useRoomContext();
  const participants = useParticipants();

  let agentId = 'DOT';
  if (persona.toLowerCase() === 'banking') {
    agentId = 'DOT';
  } else if (persona.toLowerCase() === 'insurance') {
    agentId = 'SURA';
  } else if (persona.toLowerCase() === 'sage') {
    agentId = 'SAGE';
  }
  const isAgentInRoom = participants.some(p => (p.name || p.identity) === agentId);

  console.log(`[AgentInviteButton] Rendering for ${agentId}. isAgentInRoom: ${isAgentInRoom}`);

  const toggleAgent = async () => {
    const encoder = new TextEncoder();
    const command = isAgentInRoom ? `remove ${agentId.toLowerCase()}` : `add ${agentId.toLowerCase()}`;
    const data = encoder.encode(command);
    try {
      console.log(`[AgentInviteButton] Toggling agent: ${agentId}`);
      console.log(`[AgentInviteButton] Current room state: ${room.state}`);
      console.log(`[AgentInviteButton] Local participant identity: ${room.localParticipant.identity}`);
      console.log(`[AgentInviteButton] Command string: "${command}"`);
      console.log(`[AgentInviteButton] Encoded data (hex): ${Array.from(data).map(b => b.toString(16).padStart(2, '0')).join('')}`);

      // Log if there are other participants to verify room visibility
      console.log(`[AgentInviteButton] Other participants in room: ${participants.map(p => p.identity).join(', ')}`);

      console.log('[AgentInviteButton] Calling room.localParticipant.publishData...');
      await room.localParticipant.publishData(data, {
        reliable: true,
      });

      console.log(`[AgentInviteButton] Command published successfully: ${command}`);
      // alert(`${isAgentInRoom ? 'Removal' : 'Invitation'} sent for ${agentId}. Check the agent logs for progress.`);
    } catch (error) {
      console.error('[AgentInviteButton] Error publishing data:', error);
      const msg = error instanceof Error ? error.message : String(error);
      alert(`Failed to send command: ${msg}`);
    }
  };

  return (
    <button
      onClick={toggleAgent}
      style={{
        padding: '5px 10px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: isAgentInRoom ? '#d32f2f' : '#333',
        color: 'white',
        cursor: 'pointer',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {isAgentInRoom ? <UserMinus size={14} style={{ marginRight: '5px' }} /> : <PhoneForwarded size={14} style={{ marginRight: '5px' }} />}
      {isAgentInRoom ? `Remove ${agentId}` : label}
    </button>
  );
}

export default App;
